import { Fragment, useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { adminGet, getDashboardUmbrales } from '../../api/adminClient';
import {
  DASHBOARD_UMBRALES_DEFAULTS,
  ESTADO_META,
  enrichDashboardRow,
  normalizeUmbrales,
} from '../../utils/dashboardUmbrales';

const DEVICE_LABELS = { mobile: 'Móvil', tablet: 'Tablet', desktop: 'Escritorio', unknown: 'Desconocido' };

function StatCard({ label, value, sub }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">{label}</p>
      <p className="text-3xl font-bold text-slate-800">{value}</p>
      {sub && <p className="text-sm text-slate-500 mt-1">{sub}</p>}
    </div>
  );
}

function formatFecha(fecha) {
  if (!fecha) return '—';
  const [datePart] = fecha.split(' ');
  const [y, m, d] = datePart.split('-');
  return `${d}/${m}/${y}`;
}

function formatFechaHora(fecha) {
  if (!fecha) return '—';
  const normalized = String(fecha).replace('T', ' ').slice(0, 19);
  const [datePart, timePart = ''] = normalized.split(' ');
  const [y, m, d] = datePart.split('-');
  const hm = timePart.slice(0, 5);
  return hm ? `${d}/${m}/${y} ${hm}` : `${d}/${m}/${y}`;
}

/** El API agrupa por tipo_test+test_id+tarea_id; sin tarea_id el key colisiona al reordenar. */
function rowKey(row) {
  return `${row.tipo_test}-${row.test_id}-${row.tarea_id ?? ''}`;
}

function SortHeader({ label, sortKeyName, align = 'right', sortKey, sortDir, onSort, title }) {
  const active = sortKey === sortKeyName;
  return (
    <th className={`px-4 py-3 font-medium text-slate-600 ${align === 'right' ? 'text-right' : 'text-left'}`} title={title}>
      <button
        type="button"
        onClick={() => onSort(sortKeyName)}
        className={`inline-flex items-center gap-1 cursor-pointer hover:text-slate-800 ${active ? 'text-violet-700' : ''}`}
        title={title}
      >
        {label}
        <span className="text-[10px] w-2.5 inline-block">{active ? (sortDir === 'asc' ? '▲' : '▼') : ''}</span>
      </button>
    </th>
  );
}

/** CSV con BOM UTF-8 y `;` — abre bien en Excel (AR/ES). Exporta el listado tal cual se ve. */
function downloadDashboardExcel(rows, { incluirTestings, desdeLabel }) {
  const sep = ';';
  const esc = (v) => {
    const s = v == null ? '' : String(v);
    if (/[;"\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const hoy = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  const stamp = `${hoy.getFullYear()}-${pad(hoy.getMonth() + 1)}-${pad(hoy.getDate())}`;
  const exportado = `${pad(hoy.getDate())}/${pad(hoy.getMonth() + 1)}/${hoy.getFullYear()} ${pad(hoy.getHours())}:${pad(hoy.getMinutes())}`;

  const meta = [
    ['Dashboard de uso LABPSI'],
    ['Alcance', incluirTestings ? 'Producción + testings' : `Producción desde ${desdeLabel}`],
    ['Exportado', exportado],
    [],
  ];

  const headers = [
    'Score',
    'Código',
    'Test',
    'Última vez',
    'Usos',
    'Completados',
    'Abandonados',
    '% Completitud',
    '% Acierto',
    'Estado',
    'Acción sugerida',
  ];

  const body = rows.map((r) => [
    r.score,
    r.tipo_test,
    r.nombre_test || `Test #${r.test_id}`,
    formatFecha(r.ultima_fecha),
    r.total,
    r.completadas,
    r.abandonadas,
    r.pctComp,
    r.pctAcierto ?? '',
    `${r.estadoMeta?.emoji ?? ''} ${r.estadoMeta?.label ?? r.estado}`,
    r.estadoMeta?.accion ?? '',
  ]);

  const lines = [
    ...meta.map((row) => row.map(esc).join(sep)),
    headers.map(esc).join(sep),
    ...body.map((row) => row.map(esc).join(sep)),
  ];

  const blob = new Blob([`\uFEFF${lines.join('\r\n')}`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `dashboard-uso-${stamp}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function AdminDashboard() {
  const [data, setData]       = useState(null);
  const [umbrales, setUmbrales] = useState(DASHBOARD_UMBRALES_DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [incluirTestings, setIncluirTestings] = useState(false);
  const [filtroEstado, setFiltroEstado] = useState('todos');

  const [detalle, setDetalle]           = useState(null);
  const [detalleLoading, setDetalleLoading] = useState(false);
  const [detalleKey, setDetalleKey]     = useState(null);

  const [sortKey, setSortKey] = useState('score');
  const [sortDir, setSortDir] = useState('desc');

  const toggleSort = useCallback((key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  }, [sortKey]);

  const qsTestings = incluirTestings ? '1' : '0';

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [d, cfg] = await Promise.all([
        adminGet(`/api/admin/dashboard?incluir_testings=${qsTestings}`),
        getDashboardUmbrales().catch(() => ({ umbrales: DASHBOARD_UMBRALES_DEFAULTS })),
      ]);
      setData(d);
      setUmbrales(normalizeUmbrales(cfg.umbrales || {}));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [qsTestings]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    setDetalle(null);
    setDetalleKey(null);
  }, [incluirTestings]);

  const verDetalle = useCallback(async (row) => {
    const key = rowKey(row);
    if (detalleKey === key) { setDetalle(null); setDetalleKey(null); return; }
    setDetalleKey(key);
    setDetalleLoading(true);
    try {
      const d = await adminGet(
        `/api/admin/dashboard/detalle?tipo_test=${encodeURIComponent(row.tipo_test)}&test_id=${row.test_id}&incluir_testings=${qsTestings}`,
      );
      setDetalle(d);
    } catch {
      setDetalle(null);
    } finally {
      setDetalleLoading(false);
    }
  }, [detalleKey, qsTestings]);

  const enrichedPorTest = useMemo(
    () => (data?.por_test ?? []).map((row) => enrichDashboardRow(row, umbrales)),
    [data, umbrales],
  );

  const contadoresEstado = useMemo(() => {
    const c = { todos: enrichedPorTest.length };
    for (const k of Object.keys(ESTADO_META)) c[k] = 0;
    for (const row of enrichedPorTest) c[row.estado] = (c[row.estado] || 0) + 1;
    return c;
  }, [enrichedPorTest]);

  const sortedPorTest = useMemo(() => {
    const rows = filtroEstado === 'todos'
      ? [...enrichedPorTest]
      : enrichedPorTest.filter((r) => r.estado === filtroEstado);

    const tieBreak = (a, b) => rowKey(a).localeCompare(rowKey(b));

    rows.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      let cmp = 0;
      if (av == null && bv == null) cmp = 0;
      else if (av == null) cmp = 1;
      else if (bv == null) cmp = -1;
      else if (sortKey === 'ultima_fecha') {
        const ta = new Date(av).getTime();
        const tb = new Date(bv).getTime();
        cmp = (Number.isNaN(ta) ? 0 : ta) - (Number.isNaN(tb) ? 0 : tb);
      } else if (sortKey === 'estado') {
        cmp = String(av).localeCompare(String(bv));
      } else {
        cmp = Number(av) - Number(bv);
        if (Number.isNaN(cmp)) cmp = String(av).localeCompare(String(bv));
      }
      if (cmp === 0) return tieBreak(a, b);
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return rows;
  }, [enrichedPorTest, filtroEstado, sortKey, sortDir]);

  if (loading && !data) return (
    <div className="p-8 text-slate-500">Cargando dashboard…</div>
  );
  if (error && !data) return (
    <div className="p-8 text-red-600">Error: {error}</div>
  );

  const { resumen, por_test } = data;
  const periodo = data?.periodo ?? {};
  const dispTotal = Object.values(resumen.por_dispositivo ?? {}).reduce((a, b) => a + b, 0);
  const desdeLabel = periodo.primer_uso
    ? formatFechaHora(periodo.primer_uso)
    : formatFechaHora(periodo.prod_desde);

  return (
    <div className="p-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-slate-800">Dashboard de uso</h1>
          <p className="text-sm text-slate-500 mt-1">
            {incluirTestings
              ? 'Mostrando producción + testings (histórico completo).'
              : <>Producción desde <span className="font-medium text-slate-700">{desdeLabel}</span> (primer uso registrado tras el corte).</>}
            {' '}
            <Link to="/admin/configuraciones" className="text-violet-600 hover:text-violet-800">
              Editar umbrales
            </Link>
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer select-none">
            <input
              type="checkbox"
              className="rounded border-slate-300 text-violet-600 focus:ring-violet-500"
              checked={incluirTestings}
              onChange={(e) => setIncluirTestings(e.target.checked)}
            />
            Ver también testings
          </label>
          <button
            type="button"
            onClick={() => downloadDashboardExcel(sortedPorTest, { incluirTestings, desdeLabel })}
            disabled={loading || sortedPorTest.length === 0}
            className="text-sm px-3 py-1.5 rounded border border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 cursor-pointer disabled:opacity-50"
          >
            Descargar Excel
          </button>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="text-sm px-3 py-1.5 rounded border border-slate-300 bg-white text-slate-600 hover:bg-slate-50 cursor-pointer disabled:opacity-50"
          >
            {loading ? 'Actualizando…' : 'Actualizar'}
          </button>
        </div>
      </div>

      {error && (
        <p className="mb-4 text-sm text-red-600">Error al actualizar: {error}</p>
      )}

      <div className={`grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6 ${loading ? 'opacity-60' : ''}`}>
        <StatCard label="Ejecuciones totales" value={resumen.total.toLocaleString()} />
        <StatCard
          label="Completadas"
          value={resumen.completadas.toLocaleString()}
          sub={`${resumen.tasa_completitud}% completitud`}
        />
        <StatCard label="Abandonadas" value={resumen.abandonadas.toLocaleString()} />
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">Por dispositivo</p>
          {dispTotal === 0 ? (
            <p className="text-slate-400 text-sm">Sin datos</p>
          ) : (
            <ul className="space-y-1">
              {Object.entries(resumen.por_dispositivo).map(([k, v]) => (
                <li key={k} className="flex justify-between text-sm">
                  <span className="text-slate-600">{DEVICE_LABELS[k] ?? k}</span>
                  <span className="font-medium text-slate-800">
                    {v} <span className="text-slate-400 font-normal">({Math.round(v / dispTotal * 100)}%)</span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {por_test.length > 0 && (
        <>
          <div className="flex flex-wrap gap-2 mb-3">
            <button
              type="button"
              onClick={() => setFiltroEstado('todos')}
              className={`text-xs px-2.5 py-1 rounded-full border cursor-pointer ${filtroEstado === 'todos' ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-200'}`}
            >
              Todas ({contadoresEstado.todos})
            </button>
            {Object.entries(ESTADO_META).map(([key, meta]) => (
              <button
                key={key}
                type="button"
                onClick={() => setFiltroEstado(key)}
                className={`text-xs px-2.5 py-1 rounded-full border cursor-pointer ${filtroEstado === key ? 'ring-2 ring-violet-400 border-violet-300' : 'border-slate-200'} ${meta.color}`}
              >
                {meta.emoji} {meta.label} ({contadoresEstado[key] || 0})
              </button>
            ))}
          </div>
          <details className="mb-4 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
            <summary className="cursor-pointer font-medium text-slate-700 select-none">
              ¿Qué significan Score y Estado?
            </summary>
            <div className="mt-3 space-y-3 text-xs leading-relaxed text-slate-600">
              <div>
                <p className="font-semibold text-slate-700 mb-1">Score (0–100)</p>
                <p>
                  Prioriza tareas “saludables” con datos suficientes. Es un promedio de 4 partes (25% cada una):
                </p>
                <ul className="list-disc pl-4 mt-1 space-y-0.5">
                  <li><span className="font-medium">Uso</span>: cuántas ejecuciones tiene (máximo ~20 usos).</li>
                  <li><span className="font-medium">Completitud</span>: % que terminan la tarea.</li>
                  <li><span className="font-medium">Dificultad</span>: si el % de acierto cae entre “difícil” y “fácil” (umbrales de Configuraciones).</li>
                  <li><span className="font-medium">Evidencia</span>: qué tan confiable es la muestra (máximo ~30 usos).</li>
                </ul>
                <p className="mt-1 text-slate-500">Sirve para ordenar. No es una etiqueta de acción.</p>
              </div>
              <div>
                <p className="font-semibold text-slate-700 mb-1">Estado</p>
                <p>
                  Etiqueta automática según los umbrales de{' '}
                  <Link to="/admin/configuraciones" className="text-violet-600 hover:text-violet-800">
                    Configuraciones
                  </Link>
                  : ⚪ pocos datos · 🔴 revisar · 🔵 fácil · 🟠 difícil · 🟢 buena.
                </p>
              </div>
            </div>
          </details>
        </>
      )}

      {por_test.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-400">
          {incluirTestings
            ? 'No hay datos de uso registrados todavía.'
            : 'No hay usos de producción todavía. Activá “Ver también testings” para ver el histórico previo al corte.'}
        </div>
      ) : sortedPorTest.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-400">
          No hay tareas en este estado con el filtro actual.
        </div>
      ) : (
        <div className={`bg-white rounded-xl border border-slate-200 overflow-hidden ${loading ? 'opacity-60' : ''}`}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <SortHeader label="Score"         sortKeyName="score"                     sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} title="0–100: uso + completitud + dificultad + evidencia (25% c/u)" />
                  <th className="px-4 py-3 text-left font-medium text-slate-600">Test</th>
                  <SortHeader label="Última vez"    sortKeyName="ultima_fecha" align="left" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                  <SortHeader label="Usos"           sortKeyName="total"        sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                  <SortHeader label="Completados"    sortKeyName="completadas"  sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                  <SortHeader label="Abandonados"    sortKeyName="abandonadas"  sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                  <SortHeader label="% Completitud"  sortKeyName="pctComp"      sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                  <SortHeader label="% Acierto"      sortKeyName="pctAcierto"   sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                  <SortHeader label="Estado"        sortKeyName="estado"       align="right" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {sortedPorTest.map((row) => {
                  const key = rowKey(row);
                  const { pctComp, pctAcierto, estadoMeta } = row;
                  const isOpen = detalleKey === key;

                  return (
                    <Fragment key={key}>
                      <tr className={`hover:bg-slate-50 transition-colors ${isOpen ? 'bg-violet-50' : ''}`}>
                        <td className="px-4 py-3 text-right font-medium text-slate-800">{row.score}</td>
                        <td className="px-4 py-3">
                          <span className="font-mono text-xs bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded mr-2">
                            {row.tipo_test}
                          </span>
                          <span className="text-slate-700">{row.nombre_test || `Test #${row.test_id}`}</span>
                        </td>
                        <td className="px-4 py-3 text-slate-500">{formatFecha(row.ultima_fecha)}</td>
                        <td className="px-4 py-3 text-right font-medium text-slate-800">{row.total}</td>
                        <td className="px-4 py-3 text-right text-green-700">{row.completadas}</td>
                        <td className="px-4 py-3 text-right text-red-500">{row.abandonadas}</td>
                        <td className="px-4 py-3 text-right">
                          <span className={`font-medium ${pctComp >= 70 ? 'text-green-600' : pctComp >= 40 ? 'text-amber-600' : 'text-red-500'}`}>
                            {pctComp}%
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          {pctAcierto !== null
                            ? <span className={`font-medium ${pctAcierto >= 70 ? 'text-green-600' : pctAcierto >= 40 ? 'text-amber-600' : 'text-red-500'}`}>{pctAcierto}%</span>
                            : <span className="text-slate-300">—</span>}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${estadoMeta.color}`} title={estadoMeta.accion}>
                            {estadoMeta.emoji} {estadoMeta.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => verDetalle(row)}
                            className="text-xs text-violet-600 hover:text-violet-800 cursor-pointer border-0 bg-transparent"
                          >
                            {isOpen ? 'Cerrar' : 'Detalle'}
                          </button>
                        </td>
                      </tr>

                      {isOpen && (
                        <tr>
                          <td colSpan={10} className="bg-slate-50 px-4 py-4 border-t border-violet-100">
                            <p className="text-xs text-slate-500 mb-2">{estadoMeta.accion}</p>
                            {detalleLoading ? (
                              <p className="text-slate-400 text-sm">Cargando…</p>
                            ) : !detalle || detalle.rows.length === 0 ? (
                              <p className="text-slate-400 text-sm">Sin registros.</p>
                            ) : (
                              <>
                                <p className="text-xs text-slate-500 mb-2">
                                  Últimos {detalle.rows.length} registros de {detalle.total} totales
                                </p>
                                <div className="overflow-x-auto">
                                  <table className="w-full text-xs">
                                    <thead>
                                      <tr className="text-slate-500 border-b border-slate-200">
                                        <th className="py-1.5 pr-4 text-left font-medium">Fecha</th>
                                        <th className="py-1.5 pr-4 text-left font-medium">Estado</th>
                                        <th className="py-1.5 pr-4 text-right font-medium">Items</th>
                                        <th className="py-1.5 pr-4 text-right font-medium">Correctas</th>
                                        <th className="py-1.5 pr-4 text-right font-medium">Errores</th>
                                        <th className="py-1.5 pr-4 text-left font-medium">Dispositivo</th>
                                        <th className="py-1.5 text-left font-medium">IP</th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                      {detalle.rows.map((r) => (
                                        <tr key={r.id} className="text-slate-600">
                                          <td className="py-1.5 pr-4">{r.fecha?.slice(0, 16).replace('T', ' ')}</td>
                                          <td className="py-1.5 pr-4">
                                            <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${r.completada ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                                              {r.completada ? 'Completado' : 'Abandonado'}
                                            </span>
                                          </td>
                                          <td className="py-1.5 pr-4 text-right">{r.total_items}</td>
                                          <td className="py-1.5 pr-4 text-right text-green-700">{r.correctas}</td>
                                          <td className="py-1.5 pr-4 text-right text-red-500">{r.errores}</td>
                                          <td className="py-1.5 pr-4">{DEVICE_LABELS[r.device_type] ?? r.device_type}</td>
                                          <td className="py-1.5 font-mono">{r.ip_address ?? '—'}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </>
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
